﻿import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { Drawer, EmptyState, FilterBar, LoadingState, SearchInput, useToast } from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import { useCases } from '@/hooks/useCases';
import { useLeads } from '@/hooks/useLeads';
import { type CalendarEvent as AgendaEvent, type CreateCalendarEventInput, useCalendarEvents } from '@/hooks/useCalendarEvents';
import { useTasks } from '@/hooks/useTasks';

type CalendarEventFormState = {
  title: string;
  description: string;
  eventType: string;
  clientId: string;
  caseId: string;
  leadId: string;
  taskId: string;
  startAt: string;
  endAt: string;
  location: string;
  status: string;
  responsibleUser: string;
  notes: string;
};

type AgendaViewMode = 'day' | 'week' | 'month';

const eventTypeOptions = [
  { label: 'Audiência', value: 'audiencia' },
  { label: 'Reunião', value: 'reuniao' },
  { label: 'Prazo', value: 'prazo' },
  { label: 'Retorno', value: 'retorno' },
  { label: 'Follow-up', value: 'follow_up' },
  { label: 'Atendimento', value: 'atendimento' },
  { label: 'Financeiro', value: 'financeiro' },
  { label: 'Tarefa', value: 'tarefa' },
  { label: 'Outro', value: 'outro' },
];

const statusOptions = [
  { label: 'Agendado', value: 'scheduled' },
  { label: 'Concluído', value: 'completed' },
  { label: 'Cancelado', value: 'cancelled' },
  { label: 'Arquivado', value: 'archived' },
];

const statusLabels: Record<string, string> = {
  scheduled: 'Agendado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  archived: 'Arquivado',
};

const statusClasses: Record<string, string> = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',
};

const eventTypeLabels: Record<string, string> = {
  audiencia: 'Audiência',
  reuniao: 'Reunião',
  prazo: 'Prazo',
  retorno: 'Retorno',
  follow_up: 'Follow-up',
  atendimento: 'Atendimento',
  financeiro: 'Financeiro',
  tarefa: 'Tarefa',
  outro: 'Outro',
};

const emptyFormState = (): CalendarEventFormState => ({
  title: '',
  description: '',
  eventType: 'reuniao',
  clientId: '',
  caseId: '',
  leadId: '',
  taskId: '',
  startAt: '',
  endAt: '',
  location: '',
  status: 'scheduled',
  responsibleUser: '',
  notes: '',
});

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const compactText = (...values: Array<string | null | undefined>) =>
  values.filter((value): value is string => Boolean(value && value.trim())).join(' · ');

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateOnly = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDateTimeLocal = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const nowLocalDateTime = () => formatDateTimeLocal(new Date().toISOString());

const plusMinutesLocalDateTime = (minutes: number) => {
  const next = new Date(Date.now() + minutes * 60_000);
  return formatDateTimeLocal(next.toISOString());
};

const getLocalDayKey = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createLocalDate = (date: Date, hours = 0, minutes = 0) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);

const startOfWeek = (date: Date) => {
  const local = createLocalDate(date);
  const day = local.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return createLocalDate(new Date(local.getTime() + offset * 24 * 60 * 60 * 1000));
};

const addDays = (date: Date, days: number) => createLocalDate(new Date(date.getTime() + days * 24 * 60 * 60 * 1000));

const sameDay = (left: Date, right: Date) => getLocalDayKey(left) === getLocalDayKey(right);

const sameMonth = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

const formatMonthLabel = (date: Date) =>
  date.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

const formatLongDayLabel = (date: Date) =>
  date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const toMinuteValue = (value: string | null | undefined) => {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getHours() * 60 + parsed.getMinutes();
};

const buildMonthGrid = (date: Date) => {
  const firstDay = createLocalDate(new Date(date.getFullYear(), date.getMonth(), 1));
  const gridStart = startOfWeek(firstDay);

  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

const getEventDayKey = (event: AgendaEvent) => getLocalDayKey(event.startAt);

const sortEventsByStartTime = (events: AgendaEvent[]) =>
  [...events].sort((left, right) => toMinuteValue(left.startAt) - toMinuteValue(right.startAt));

const buildFormStateFromEvent = (event: AgendaEvent): CalendarEventFormState => ({
  title: event.title,
  description: event.description ?? '',
  eventType: event.eventType || 'outro',
  clientId: event.clientId ?? '',
  caseId: event.caseId ?? '',
  leadId: event.leadId ?? '',
  taskId: event.taskId ?? '',
  startAt: formatDateTimeLocal(event.startAt),
  endAt: formatDateTimeLocal(event.endAt),
  location: event.location ?? '',
  status: event.status,
  responsibleUser: event.responsibleUser ?? '',
  notes: event.notes ?? '',
});

const buildCreatePayload = (form: CalendarEventFormState): CreateCalendarEventInput => ({
  title: form.title.trim(),
  description: toNullableText(form.description),
  eventType: toNullableText(form.eventType),
  clientId: toNullableText(form.clientId),
  caseId: toNullableText(form.caseId),
  leadId: toNullableText(form.leadId),
  taskId: toNullableText(form.taskId),
  startAt: new Date(form.startAt).toISOString(),
  endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
  location: toNullableText(form.location),
  status: form.status,
  responsibleUser: toNullableText(form.responsibleUser),
  notes: toNullableText(form.notes),
  isActive: form.status !== 'archived',
});

const AgendaPage: FC = () => {
  const { addToast } = useToast();
  const { clients, clientsLoading, clientsError, users } = useAppContext();
  const { cases, isLoadingCases } = useCases();
  const { leads, isLoadingLeads } = useLeads();
  const { tasks, isLoadingTasks } = useTasks();
  const {
    calendarEvents,
    loading,
    error,
    loadCalendarEvents,
    refetch,
    createCalendarEvent,
    updateCalendarEvent,
    archiveCalendarEvent,
  } = useCalendarEvents({ autoLoad: false });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [viewMode, setViewMode] = useState<AgendaViewMode>('month');
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<AgendaEvent | null>(null);
  const [form, setForm] = useState<CalendarEventFormState>(emptyFormState());
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);

  useEffect(() => {
    void loadCalendarEvents({
      includeArchived: statusFilter === 'archived',
    });
  }, [loadCalendarEvents, statusFilter]);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((client: any) => client?.status !== 'deleted')
        .map((client: any) => ({
          value: client.id,
          label: client.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [clients]
  );

  const caseOptions = useMemo(
    () =>
      cases
        .filter((caseItem) => caseItem.status !== 'archived')
        .map((caseItem) => ({
          value: caseItem.id,
          label: compactText(caseItem.title, caseItem.clientName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [cases]
  );

  const leadOptions = useMemo(
    () =>
      leads
        .filter((lead) => lead.status !== 'archived')
        .map((lead) => ({
          value: lead.id,
          label: compactText(lead.name, lead.legalProductName),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [leads]
  );

  const taskOptions = useMemo(
    () =>
      tasks
        .filter((task) => task.status !== 'archived')
        .map((task) => ({
          value: task.id,
          label: compactText(task.title, task.clientName, task.caseTitle),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [tasks]
  );

  const responsibleOptions = useMemo(() => {
    const names = new Set<string>();

    users.forEach((user) => {
      names.add(user.name);
    });

    calendarEvents.forEach((event) => {
      if (event.responsibleUser) {
        names.add(event.responsibleUser);
      }
    });

    return [...names]
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((name) => ({ label: name, value: name }));
  }, [calendarEvents, users]);

  const visibleEvents = useMemo(() => {
    const query = normalizeSearchText(search);

    return calendarEvents.filter((event) => {
      if (!statusFilter) {
        if (event.status === 'archived') {
          return false;
        }
      } else if (event.status !== statusFilter) {
        return false;
      }

      if (eventTypeFilter && event.eventType !== eventTypeFilter) {
        return false;
      }

      if (responsibleFilter && event.responsibleUser !== responsibleFilter) {
        return false;
      }

      if (startDateFilter && formatDateOnly(event.startAt) < startDateFilter) {
        return false;
      }

      if (endDateFilter && formatDateOnly(event.startAt) > endDateFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = normalizeSearchText(
        [
          event.title,
          event.description,
          event.eventType,
          event.clientName,
          event.caseTitle,
          event.leadName,
          event.taskTitle,
          event.location,
          event.responsibleUser,
          event.notes,
        ]
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(query);
    });
  }, [calendarEvents, endDateFilter, eventTypeFilter, responsibleFilter, search, startDateFilter, statusFilter]);

  const selectedDateLocal = useMemo(() => createLocalDate(selectedDate), [selectedDate]);
  const selectedDayKey = useMemo(() => getLocalDayKey(selectedDateLocal), [selectedDateLocal]);
  const weekStartDate = useMemo(() => startOfWeek(selectedDateLocal), [selectedDateLocal]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStartDate, index)), [weekStartDate]);
  const monthGrid = useMemo(() => buildMonthGrid(selectedDateLocal), [selectedDateLocal]);

  const selectedDayEvents = useMemo(
    () => sortEventsByStartTime(visibleEvents.filter((event) => getEventDayKey(event) === selectedDayKey)),
    [selectedDayKey, visibleEvents]
  );

  const weekEventsByDay = useMemo(() => {
    return weekDays.reduce<Record<string, AgendaEvent[]>>((accumulator, day) => {
      const key = getLocalDayKey(day);
      accumulator[key] = sortEventsByStartTime(visibleEvents.filter((event) => getEventDayKey(event) === key));
      return accumulator;
    }, {});
  }, [visibleEvents, weekDays]);

  const monthEventsByDay = useMemo(() => {
    return monthGrid.reduce<Record<string, AgendaEvent[]>>((accumulator, day) => {
      const key = getLocalDayKey(day);
      accumulator[key] = sortEventsByStartTime(visibleEvents.filter((event) => getEventDayKey(event) === key));
      return accumulator;
    }, {});
  }, [monthGrid, visibleEvents]);

  const periodTitle = useMemo(() => {
    if (viewMode === 'day') {
      return formatLongDayLabel(selectedDateLocal);
    }

    if (viewMode === 'week') {
      const weekEnd = addDays(weekStartDate, 6);
      return `${weekStartDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} a ${weekEnd.toLocaleDateString(
        'pt-BR',
        { day: '2-digit', month: 'short', year: 'numeric' }
      )}`;
    }

    return formatMonthLabel(selectedDateLocal);
  }, [selectedDateLocal, viewMode, weekStartDate]);

  const periodDescription = useMemo(() => {
    if (viewMode === 'day') {
      return 'Eventos do dia selecionado, ordenados por horário.';
    }

    if (viewMode === 'week') {
      return 'Resumo dos próximos 7 dias com eventos agrupados por data.';
    }

    return 'Visão mensal dos compromissos reais do escritório.';
  }, [viewMode]);

  const hasEventsInCurrentView = useMemo(() => {
    if (viewMode === 'day') {
      return selectedDayEvents.length > 0;
    }

    if (viewMode === 'week') {
      return weekDays.some((day) => (weekEventsByDay[getLocalDayKey(day)] ?? []).length > 0);
    }

    return monthGrid.some((day) => (monthEventsByDay[getLocalDayKey(day)] ?? []).length > 0);
  }, [monthEventsByDay, monthGrid, selectedDayEvents.length, viewMode, weekDays, weekEventsByDay]);

  const isLoadingAgenda = (clientsLoading || isLoadingCases || isLoadingLeads || isLoadingTasks || loading) && !hasEventsInCurrentView;

  const openCreateDrawer = () => {
    if (isSavingEvent) {
      return;
    }

    const selectedStart = createLocalDate(selectedDateLocal, new Date().getHours(), new Date().getMinutes());
    const startAt = formatDateTimeLocal(selectedStart.toISOString()) || nowLocalDateTime();
    const endAt = formatDateTimeLocal(new Date(selectedStart.getTime() + 60 * 60 * 1000).toISOString()) || plusMinutesLocalDateTime(60);

    setEditingEvent(null);
    setForm({
      ...emptyFormState(),
      responsibleUser: users[0]?.name ?? '',
      startAt,
      endAt,
    });
    setEditorOpen(true);
  };

  const openEditDrawer = (event: AgendaEvent) => {
    if (isSavingEvent || busyEventId) {
      return;
    }

    setEditingEvent(event);
    setForm(buildFormStateFromEvent(event));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSavingEvent) {
      return;
    }

    setEditorOpen(false);
    setEditingEvent(null);
    setForm(emptyFormState());
  };

  const refreshAgenda = async () => {
    await refetch();
  };

  const saveEvent = async () => {
    if (isSavingEvent) {
      return;
    }

    if (!form.title.trim()) {
      addToast('Preencha o título do evento.', 'warning');
      return;
    }

    if (!form.startAt) {
      addToast('Preencha a data e hora de início.', 'warning');
      return;
    }

    const startTimestamp = new Date(form.startAt).getTime();
    if (Number.isNaN(startTimestamp)) {
      addToast('Informe uma data/hora de início válida.', 'warning');
      return;
    }

    if (form.endAt) {
      const endTimestamp = new Date(form.endAt).getTime();
      if (Number.isNaN(endTimestamp)) {
        addToast('Informe uma data/hora de fim válida.', 'warning');
        return;
      }

      if (endTimestamp < startTimestamp) {
        addToast('A data/hora de fim não pode ser anterior ao início.', 'warning');
        return;
      }
    }

    setIsSavingEvent(true);

    try {
      const payload = buildCreatePayload(form);

      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, payload);
        addToast('Evento atualizado com sucesso.', 'success');
      } else {
        await createCalendarEvent(payload);
        addToast('Evento criado com sucesso.', 'success');
      }

      await refetch();
      setEditorOpen(false);
      setEditingEvent(null);
      setForm(emptyFormState());
    } catch (saveError: any) {
      addToast(saveError?.message || 'Não foi possível salvar o evento.', 'error');
    } finally {
      setIsSavingEvent(false);
    }
  };

  const updateEventStatus = async (event: AgendaEvent, status: string) => {
    if (busyEventId || isSavingEvent) {
      return;
    }

    setBusyEventId(event.id);

    try {
      await updateCalendarEvent(event.id, { status, isActive: status !== 'archived' });
      addToast(
        status === 'completed'
          ? 'Evento marcado como concluído.'
          : status === 'cancelled'
          ? 'Evento cancelado com sucesso.'
          : 'Evento reativado para agendamento.',
        'success'
      );
      await refetch();
    } catch (actionError: any) {
      addToast(actionError?.message || 'Não foi possível atualizar o status do evento.', 'error');
    } finally {
      setBusyEventId(null);
    }
  };

  const archiveEvent = async (event: AgendaEvent) => {
    if (busyEventId || isSavingEvent) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar este evento? Esta ação removerá o evento da lista principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setBusyEventId(event.id);

    try {
      await archiveCalendarEvent(event.id);
      addToast('Evento arquivado com sucesso.', 'success');
      await refetch();
    } catch (archiveError: any) {
      addToast(archiveError?.message || 'Não foi possível arquivar o evento.', 'error');
    } finally {
      setBusyEventId(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setEventTypeFilter('');
    setResponsibleFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const setToday = () => {
    setSelectedDate(new Date());
  };

  const shiftPeriod = (direction: -1 | 1) => {
    if (viewMode === 'day') {
      setSelectedDate((current) => addDays(current, direction));
      return;
    }

    if (viewMode === 'week') {
      setSelectedDate((current) => addDays(current, direction * 7));
      return;
    }

    setSelectedDate((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  };

  const openDayView = (date: Date) => {
    setSelectedDate(date);
    setViewMode('day');
  };

  const renderEventCard = (event: AgendaEvent, variant: 'compact' | 'default' = 'default') => {
    const eventIsBusy = busyEventId === event.id;
    const statusLabel = statusLabels[event.status] ?? event.status;
    const statusClass = statusClasses[event.status] ?? statusClasses.scheduled;
    const eventTypeLabel = eventTypeLabels[event.eventType] ?? event.eventType;
    const showRestoreAction = event.status !== 'scheduled' && event.status !== 'archived';

    return (
      <div
        key={event.id}
        className={`bg-background-50 border border-background-200 rounded-xl ${
          variant === 'compact' ? 'p-3' : 'p-4'
        } hover:border-primary-200 transition-colors`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className={`font-semibold text-foreground-800 ${variant === 'compact' ? 'text-xs' : 'text-sm'}`}>{event.title}</h4>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${statusClass}`}>
              {statusLabel}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap">
              {eventTypeLabel}
            </span>
          </div>

          <p className="text-xs text-foreground-500">
            {formatDateTime(event.startAt)}
            {event.endAt ? ` · até ${formatDateTime(event.endAt)}` : ''}
          </p>

          {event.description && <p className="text-sm text-foreground-600">{event.description}</p>}

          <div className="flex flex-wrap gap-2 text-xs text-foreground-500">
            {event.clientName && <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Cliente: {event.clientName}</span>}
            {event.caseTitle && <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Caso: {event.caseTitle}</span>}
            {event.leadName && <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Lead: {event.leadName}</span>}
            {event.taskTitle && <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Tarefa: {event.taskTitle}</span>}
            {event.location && <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Local: {event.location}</span>}
            {event.responsibleUser && (
              <span className="px-2 py-1 rounded-full bg-background-100 border border-background-200">Responsável: {event.responsibleUser}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => openEditDrawer(event)}
              disabled={isSavingEvent || eventIsBusy}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className="ri-edit-line mr-1"></i>Editar
            </button>
            {event.status === 'scheduled' && (
              <>
                <button
                  type="button"
                  onClick={() => void updateEventStatus(event, 'completed')}
                  disabled={isSavingEvent || eventIsBusy}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-checkbox-circle-line mr-1"></i>Concluir
                </button>
                <button
                  type="button"
                  onClick={() => void updateEventStatus(event, 'cancelled')}
                  disabled={isSavingEvent || eventIsBusy}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-close-circle-line mr-1"></i>Cancelar
                </button>
              </>
            )}
            {showRestoreAction && (
              <button
                type="button"
                onClick={() => void updateEventStatus(event, 'scheduled')}
                disabled={isSavingEvent || eventIsBusy}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-refresh-line mr-1"></i>Voltar para agendado
              </button>
            )}
            {event.status !== 'archived' && (
              <button
                type="button"
                onClick={() => void archiveEvent(event)}
                disabled={isSavingEvent || eventIsBusy}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-archive-line mr-1"></i>
                {eventIsBusy ? 'Arquivando...' : 'Arquivar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Agenda Interna</h2>
            <p className="text-sm text-foreground-500 mt-0.5">Compromissos, prazos, retornos e eventos do escritório</p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar evento..."
            className="w-full sm:max-w-sm"
          />

          <FilterBar
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: statusOptions,
                value: statusFilter,
                onChange: setStatusFilter,
              },
              {
                key: 'type',
                label: 'Tipo',
                options: eventTypeOptions,
                value: eventTypeFilter,
                onChange: setEventTypeFilter,
              },
              {
                key: 'responsible',
                label: 'Responsável',
                options: responsibleOptions,
                value: responsibleFilter,
                onChange: setResponsibleFilter,
              },
            ]}
          />

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-foreground-500 font-medium mb-1">Período inicial</label>
                <input
                  type="date"
                  value={startDateFilter}
                  onChange={(event) => setStartDateFilter(event.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-500 font-medium mb-1">Período final</label>
                <input
                  type="date"
                  value={endDateFilter}
                  onChange={(event) => setEndDateFilter(event.target.value)}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Limpar filtros
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-background-200 bg-background-50 p-3 md:p-4 space-y-4">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-foreground-800">{periodTitle}</h3>
              <p className="text-sm text-foreground-500 mt-0.5">{periodDescription}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(['day', 'week', 'month'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                    viewMode === mode
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:bg-background-100'
                  }`}
                >
                  {mode === 'day' ? 'Dia' : mode === 'week' ? 'Semana' : 'Mês'}
                </button>
              ))}

              <button
                type="button"
                onClick={() => shiftPeriod(-1)}
                className="p-2 rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                aria-label="Período anterior"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                type="button"
                onClick={setToday}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Hoje
              </button>
              <button
                type="button"
                onClick={() => shiftPeriod(1)}
                className="p-2 rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
                aria-label="Próximo período"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
              <button
                onClick={() => void refreshAgenda()}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 text-foreground-600 rounded-lg text-xs font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i> Atualizar
              </button>
              <button
                onClick={openCreateDrawer}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Novo Evento
              </button>
            </div>
          </div>

          {(clientsError || error) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <i className="ri-alert-line mt-0.5"></i>
              <div className="flex-1">
                <p className="font-medium">Alguns dados auxiliares não foram carregados como esperado.</p>
                <p className="text-amber-700 mt-0.5">{clientsError || error}</p>
              </div>
            </div>
          )}

          {isLoadingAgenda ? (
            <LoadingState message="Carregando agenda interna real..." />
          ) : !hasEventsInCurrentView ? (
            <EmptyState
              icon="ri-calendar-line"
              title="Nenhum evento encontrado"
              description="Cadastre um novo compromisso ou ajuste os filtros para visualizar eventos existentes."
              action={{
                label: 'Novo Evento',
                onClick: openCreateDrawer,
                icon: 'ri-add-line',
              }}
            />
          ) : viewMode === 'day' ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-background-200 bg-background-50 p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">{formatLongDayLabel(selectedDateLocal)}</h4>
                    <p className="text-xs text-foreground-500">{selectedDayEvents.length} evento(s) neste dia</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {selectedDayEvents.map((event) => renderEventCard(event))}
                </div>
              </div>
            </div>
          ) : viewMode === 'week' ? (
            <div className="grid gap-3 xl:grid-cols-7">
              {weekDays.map((day) => {
                const key = getLocalDayKey(day);
                const dayEvents = weekEventsByDay[key] ?? [];
                const isToday = sameDay(day, new Date());
                const isSelected = sameDay(day, selectedDateLocal);

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => openDayView(day)}
                    className={`text-left rounded-xl border p-3 transition-colors hover:border-primary-200 ${
                      isSelected ? 'border-primary-300 bg-primary-50/40' : 'border-background-200 bg-background-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-foreground-500">{day.toLocaleDateString('pt-BR', { weekday: 'short' })}</p>
                        <p className="text-base font-semibold text-foreground-800">{day.getDate()}</p>
                      </div>
                      {isToday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50">Hoje</span>}
                    </div>

                    <p className="text-[11px] text-foreground-500 mt-1 mb-3">{day.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>

                    <div className="space-y-2">
                      {dayEvents.length === 0 ? (
                        <p className="text-xs text-foreground-400">Sem eventos</p>
                      ) : (
                        dayEvents.slice(0, 4).map((event) => (
                          <div key={event.id} className="rounded-lg border border-background-200 bg-background-50 p-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-semibold text-foreground-700">
                                {new Date(event.startAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${statusClasses[event.status] ?? statusClasses.scheduled}`}>
                                {statusLabels[event.status] ?? event.status}
                              </span>
                            </div>
                            <p className="text-xs font-medium text-foreground-700 mt-1 line-clamp-2">{event.title}</p>
                          </div>
                        ))
                      )}
                      {dayEvents.length > 4 && <p className="text-[11px] text-foreground-500">+ {dayEvents.length - 4} evento(s)</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-background-200 bg-background-50 p-4">
              <div className="grid grid-cols-7 gap-2 text-[11px] font-semibold text-foreground-500 mb-3">
                {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((weekday) => (
                  <div key={weekday} className="text-center">
                    {weekday}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {monthGrid.map((day) => {
                  const key = getLocalDayKey(day);
                  const dayEvents = monthEventsByDay[key] ?? [];
                  const isToday = sameDay(day, new Date());
                  const isCurrentMonth = sameMonth(day, selectedDateLocal);
                  const isSelected = sameDay(day, selectedDateLocal);

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => openDayView(day)}
                      className={`min-h-[118px] rounded-xl border p-2 text-left transition-colors hover:border-primary-200 ${
                        isSelected ? 'border-primary-300 bg-primary-50/40' : 'border-background-200 bg-background-50'
                      } ${isCurrentMonth ? '' : 'opacity-50'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold ${isToday ? 'text-primary-600' : 'text-foreground-700'}`}>
                          {day.getDate()}
                        </span>
                        {isToday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50">Hoje</span>}
                      </div>

                      <div className="mt-2 space-y-1">
                        {dayEvents.slice(0, 3).map((event) => (
                          <div key={event.id} className="rounded-lg border border-background-200 bg-background-50 px-2 py-1">
                            <p className="text-[11px] font-medium text-foreground-700 truncate">{event.title}</p>
                            <p className="text-[10px] text-foreground-500">
                              {new Date(event.startAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        ))}
                        {dayEvents.length > 3 && <p className="text-[10px] text-foreground-500">+ {dayEvents.length - 3} evento(s)</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <Drawer
          isOpen={editorOpen}
          onClose={closeEditor}
          title={editingEvent ? `Editar: ${editingEvent.title}` : 'Novo Evento'}
          width="w-[680px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void saveEvent()}
                disabled={isSavingEvent}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSavingEvent ? 'Salvando...' : 'Salvar Evento'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setForm((previous) => ({ ...previous, title: event.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={form.description}
                onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tipo do evento</label>
                <select
                  value={form.eventType}
                  onChange={(event) => setForm((previous) => ({ ...previous, eventType: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {eventTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(event) => setForm((previous) => ({ ...previous, status: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Data/hora de início *</label>
                <input
                  type="datetime-local"
                  value={form.startAt}
                  onChange={(event) => setForm((previous) => ({ ...previous, startAt: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Data/hora de fim</label>
                <input
                  type="datetime-local"
                  value={form.endAt}
                  onChange={(event) => setForm((previous) => ({ ...previous, endAt: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Cliente opcional</label>
                <select
                  value={form.clientId}
                  onChange={(event) => setForm((previous) => ({ ...previous, clientId: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {clientOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Caso opcional</label>
                <select
                  value={form.caseId}
                  onChange={(event) => setForm((previous) => ({ ...previous, caseId: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {caseOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Lead opcional</label>
                <select
                  value={form.leadId}
                  onChange={(event) => setForm((previous) => ({ ...previous, leadId: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {leadOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tarefa opcional</label>
                <select
                  value={form.taskId}
                  onChange={(event) => setForm((previous) => ({ ...previous, taskId: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {taskOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Responsável</label>
              <input
                type="text"
                value={form.responsibleUser}
                onChange={(event) => setForm((previous) => ({ ...previous, responsibleUser: event.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Nome do responsável"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Local</label>
              <input
                type="text"
                value={form.location}
                onChange={(event) => setForm((previous) => ({ ...previous, location: event.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Escritório, fórum, link, sala..."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default AgendaPage;
